import api from './api';

export interface AnalyzeRequest {
  repository_url: string;
  access_token?: string;
}

export interface Bug {
  severity: 'low' | 'medium' | 'high' | 'critical';
  file: string;
  line?: number;
  description: string;
  suggestion?: string;
}

export interface AnalysisResult {
  project_name: string;
  description: string;
  project_type: string;
  languages: string[];
  frameworks: string[];
  bugs_found: Bug[];
  dependencies: {
    total: number;
    outdated: number;
    vulnerable: number;
  };
  code_quality: {
    score: number;
    issues: string[];
    suggestions: string[];
  };
  has_tests: boolean;
  test_coverage?: number;
  has_documentation: boolean;
  repository_url: string;
}

export interface AnalyzeResponse {
  success: boolean;
  message: string;
  analysis: AnalysisResult;
}

export interface ClientOption {
  value: number;
  label: string;
  website?: string;
}

export interface NewClient {
  name: string;
  website?: string;
  industry?: string;
  description?: string;
}

export interface ImportRequest {
  repository_url: string;
  access_token?: string;
  analysis: AnalysisResult;
  client_id?: number;
  new_client?: NewClient;
  project_manager_id?: number;
  generate_specs?: boolean;
}

export interface ImportResponse {
  success: boolean;
  message: string;
  project: {
    id: number;
    name: string;
    status: string;
  };
}

export interface ServiceStatus {
  analyzer: {
    status: string;
    claude_available: boolean;
    openai_available: boolean;
  };
  speckit: {
    status: string;
  };
}

/**
 * Import Project Service
 * Handles communication with the import project API
 */
class ImportProjectService {
  /**
   * Get status of analyzer and speckit services
   */
  async getStatus(): Promise<ServiceStatus> {
    const response = await api.get('/admin/import-project/status');
    return response.data;
  }

  /**
   * Analyze a GitHub repository
   */
  async analyzeRepository(request: AnalyzeRequest): Promise<AnalysisResult> {
    const response = await api.post<AnalyzeResponse>(
      '/admin/import-project/analyze',
      request
    );
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to analyze repository');
    }
    
    return response.data.analysis;
  }

  /**
   * Get all available clients
   */
  async getClients(): Promise<ClientOption[]> {
    const response = await api.get('/admin/import-project/clients');
    return response.data.clients;
  }

  /**
   * Import a project from analysis results
   */
  async importProject(request: ImportRequest): Promise<ImportResponse['project']> {
    const response = await api.post<ImportResponse>(
      '/admin/import-project/import',
      request
    );
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to import project');
    }
    
    return response.data.project;
  }
}

export default new ImportProjectService();
